import { message } from "antd";
import { Dispatch } from "redux";

import { handBookApi } from "./api";
import { HANDBOOK_TEMPLATE_LVL, HandbookExpenditureLocalChanges, handBookActions } from "./slice";
import { RootState } from "app/store/rootReducer";

import {
  HANDBOOK_EXPENDITURE_APPROVAL_STATUS,
  ICreateTemplateSection,
  IHandBook,
  IHandBookRequest,
  IHandBookTemplate,
} from "./types";

import { errorCatcher } from "utils/helpers/errorCatcher";
import { generateStorageKey } from "utils/helpers/generateStorageKey";

export const getHandbooks =
  (
    { limit, offset, ...filter }: IHandBookRequest = { status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED },
    controller: AbortController
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    //TODO поправить типы generateStorageKey
    const key = generateStorageKey(filter as any);
    if (!getState().handBookReducer.handbooks[key]) {
      dispatch(handBookActions.setIsLoading({ key, status: true }));
    }
    handBookApi
      .getList({ limit, offset, ...filter }, controller)
      .then(({ data }) => {
        dispatch(handBookActions.setItems({ key, items: data }));
      })
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key, status: false }));
      });
  };

export const getHandbooksMore =
  (filters: IHandBookRequest = { status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const loadingKey = generateStorageKey(filters as any);
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true })); // Блокируем кнопку "Показать ещё"
    const { limit, offset, ...pushKeyObject } = filters;
    //TODO поправить типы generateStorageKey
    const pushKey = generateStorageKey(pushKeyObject as any);
    if (!getState().handBookReducer.handbooks[pushKey]) {
      dispatch(handBookActions.setIsLoading({ key: pushKey, status: true }));
    }
    handBookApi
      .getList(filters)
      .then(({ data }) => {
        dispatch(handBookActions.setItemsMore({ key: pushKey, items: data }));
      })
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: pushKey, status: false }));
        dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
      });
  };

export const getHandbooksAggregate = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.setIsLoading({ key: "getHandbooksAggregate", status: true }));
  handBookApi
    .getAggregate()
    .then(({ data }) => {
      dispatch(handBookActions.setAggregate(data));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "getHandbooksAggregate", status: false }));
    });
};

export const editHandbook = (id: number, data: any) => (dispatch: Dispatch) => {
  const key = generateStorageKey({ id, type: "mutate" });
  dispatch(handBookActions.setIsLoading({ key, status: true }));
  return handBookApi
    .patch(id, data)
    .then(({ data }) => {
      dispatch(handBookActions.updateItem(data));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const deleteHandbook = (id: number) => (dispatch: Dispatch) => {
  const key = generateStorageKey({ id, type: "mutate" });
  dispatch(handBookActions.setIsLoading({ key, status: true }));
  return handBookApi
    .delete(id)
    .then(({ data }) => {
      dispatch(handBookActions.deleteItem(id));
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const cancelEditingHandbook = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.clearEditings());
  dispatch(handBookActions.setEditing(false));
};

export const cancelEditingHandbookTemplates = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.clearTemplateEditings());
  dispatch(handBookActions.setEditingTemplates(false));
};

export const applyEditingHandbook = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const handbookEditings = getState().handBookReducer.editings;
  // @ts-ignore
  const deletions = Object.keys(handbookEditings.deletions).map((id) => dispatch(deleteHandbook(+id)));
  const editings = Object.entries(handbookEditings.changes).map(([id, change]) =>
    // @ts-ignore | Пропускаем patch удалённых строк
    handbookEditings.deletions[+id] ? Promise.resolve() : dispatch(editHandbook(+id, change))
  );
  await Promise.allSettled([...deletions, ...editings]);
  message.success("Справочник отредактирован");
  dispatch(handBookActions.clearEditings());
  dispatch(handBookActions.setEditing(false));
  dispatch(handBookActions.invalidateKey());
};

export const applyEditingHandbookTemplates = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const handbookTemplateEditings = getState().handBookReducer.templateEditings;
  // Шаблоны
  const templateDeletions = Object.keys(handbookTemplateEditings.templates.deletions).map((id) =>
    handBookApi.deleteTemplate(+id).catch(errorCatcher)
  );
  const templateEditings = Object.entries(handbookTemplateEditings.templates.changes).map(([id, change]) =>
    // Пропускаем patch удалённых строк
    handbookTemplateEditings.templates.deletions[+id]
      ? Promise.resolve()
      : handBookApi.patchTemplate(+id, change).catch(errorCatcher)
  );
  // Секции
  const sectionDeletions = Object.keys(handbookTemplateEditings.sections.deletions).map((id) =>
    handBookApi.deleteTemplateSection(id).catch(errorCatcher)
  );
  const sectionEditings = Object.entries(handbookTemplateEditings.sections.changes).map(([id, change]) =>
    // Пропускаем patch удалённых строк
    handbookTemplateEditings.sections.deletions[+id]
      ? Promise.resolve()
      : handBookApi.patchTemplateSection(+id, change).catch(errorCatcher)
  );
  // Расценки
  const expenditureDeletions = Object.keys(handbookTemplateEditings.expenditures.deletions).map((id) =>
    handBookApi.deleteTemplateExpenditure(+id).catch(errorCatcher)
  );
  const expenditureEditings = Object.entries(handbookTemplateEditings.expenditures.changes)
    .filter(
      ([id, _]) =>
        // Пропускаем patch удалённых строк
        !handbookTemplateEditings.expenditures.deletions[+id]
    )
    .flatMap(([id, changes]: [string, Partial<HandbookExpenditureLocalChanges>]) => {
      let promises: Promise<any>[] = [];
      if (changes.materialsToDelete) {
        promises = promises.concat(
          changes.materialsToDelete.map((m) => handBookApi.deleteTemplateExpenditure(+m).catch(errorCatcher))
        );
      }
      if (changes.newMaterialsFromHandbook) {
        promises = promises.concat(
          changes.newMaterialsFromHandbook.map((m) => handBookApi.createTemplateExpenditure(m).catch(errorCatcher))
        );
      }
      if (changes.newManualMaterials) {
        promises = promises.concat(
          changes.newManualMaterials.map((m) =>
            handBookApi
              .post(m.manual_expenditure)
              .then(({ data: newManualExpenditure }) =>
                handBookApi
                  .createTemplateExpenditure({
                    ...m,
                    manual_expenditure_id: newManualExpenditure.id,
                  })
                  .catch(errorCatcher)
              )
              .catch(errorCatcher)
          )
        );
      }
      return promises;
    });
  await Promise.allSettled([
    ...templateDeletions,
    ...templateEditings,
    ...sectionDeletions,
    ...sectionEditings,
    ...expenditureDeletions,
    ...expenditureEditings,
  ]);
  message.success("Шаблоны отредактированы");
  dispatch(handBookActions.clearTemplateEditings());
  dispatch(handBookActions.setEditing(false));
  dispatch(handBookActions.invalidateKey());
};

export const localChangeHandbookTemplate =
  (id: number, change: Partial<IHandBookTemplate>, lvl: HANDBOOK_TEMPLATE_LVL) => (dispatch: Dispatch) => {
    dispatch(handBookActions.localChangeTemplateItem({ id, change, lvl }));
  };

export const localDeleteHandbook = (id: number) => (dispatch: Dispatch) => {
  dispatch(handBookActions.localDeleteItem(id));
};

export const localChangeHandbook = (id: number, change: Partial<IHandBook>) => (dispatch: Dispatch) => {
  dispatch(handBookActions.localChangeItem({ id, change }));
};

export const localDeleteCheckedHandbooks = () => (dispatch: Dispatch, getState: () => RootState) => {
  const checkedHandbooks = getState().handBookReducer.editings.checked;
  Object.entries(checkedHandbooks).forEach(([handbookId, isChecked]) => {
    if (isChecked) {
      dispatch(handBookActions.localDeleteItem(+handbookId));
      dispatch(handBookActions.localCheckItem({ id: +handbookId, isChecked: false }));
    }
  });
};

export const localDeleteCheckedHandbookTemplates = () => (dispatch: Dispatch, getState: () => RootState) => {
  const editings = getState().handBookReducer.templateEditings;
  // Шаблоны
  Object.entries(editings.templates.checked).forEach(([id, isChecked]) => {
    if (isChecked) {
      dispatch(handBookActions.localDeleteTemplateItem({ id: +id, lvl: HANDBOOK_TEMPLATE_LVL.TEMPLATES }));
      dispatch(
        handBookActions.localCheckTemplateItem({ id: +id, isChecked: false, lvl: HANDBOOK_TEMPLATE_LVL.TEMPLATES })
      );
    }
  });
  // Секции
  Object.entries(editings.sections.checked).forEach(([id, isChecked]) => {
    if (isChecked) {
      dispatch(handBookActions.localDeleteTemplateItem({ id: +id, lvl: HANDBOOK_TEMPLATE_LVL.SECTIONS }));
      dispatch(
        handBookActions.localCheckTemplateItem({ id: +id, isChecked: false, lvl: HANDBOOK_TEMPLATE_LVL.SECTIONS })
      );
    }
  });
  // Расценки
  Object.entries(editings.expenditures.checked).forEach(([id, isChecked]) => {
    if (isChecked) {
      dispatch(handBookActions.localDeleteTemplateItem({ id: +id, lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES }));
      dispatch(
        handBookActions.localCheckTemplateItem({ id: +id, isChecked: false, lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES })
      );
    }
  });
};

export const createHandbookItem = (vals: Partial<IHandBook>) => (dispatch: Dispatch, getState: () => RootState) => {
  const key = getState().handBookReducer._lastUsedKey;
  return handBookApi
    .post(vals)
    .then(({ data }) => {
      dispatch(handBookActions.addItem({ item: data, key }));
      dispatch(handBookActions.localDeleteAddingItem(vals.id!));
      message.success("Запись успешно добавлена");
    })
    .catch(errorCatcher);
};

export const createHandTemplate = (vals: any, onSuccess?: () => void) => (dispatch: Dispatch) => {
  dispatch(handBookActions.setIsLoading({ key: "createTemplate", status: true }));
  handBookApi
    .createTemplate(vals)
    .then(() => {
      dispatch(handBookActions.invalidateKey());
      onSuccess?.();
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "createTemplate", status: false }));
    });
};

export const getHandbookTemplates = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter);
  if (!getState().handBookReducer.templates[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key, status: true }));
  }
  handBookApi
    .getTemplatesList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplates({ key, templates: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const getHandbookTemplatesMore = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey({ ...restFilter });
  const loadingKey = generateStorageKey({ ...restFilter, type: "moreTemplates" });
  if (!getState().handBookReducer.templates[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true }));
  }
  handBookApi
    .getTemplatesList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplatesMore({ key, templates: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
    });
};

export const deleteHandbookTemplate =
  (id: any, onSuccess?: () => void) => (dispatch: Dispatch, getState: () => RootState) => {
    const key = generateStorageKey({ id, type: "mutateHandbookTemplate" });
    dispatch(handBookActions.setIsLoading({ key, status: true }));
    handBookApi
      .deleteTemplate(id)
      .then(() => {
        dispatch(handBookActions.invalidateKey());
        onSuccess?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key, status: false }));
      });
  };

export const editHandbookTemplate =
  (id: any, data: any, onSuccess?: () => void) => (dispatch: Dispatch, getState: () => RootState) => {
    const key = generateStorageKey({ id, type: "mutateHandbookTemplate" });
    dispatch(handBookActions.setIsLoading({ key, status: true }));
    handBookApi
      .patchTemplate(id, data)
      .then(() => {
        dispatch(handBookActions.invalidateKey());
        onSuccess?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key, status: false }));
      });
  };

export const approveHandbookTemplate = (id: number, onSuccess?: () => void) => (dispatch: Dispatch) => {
  const key = generateStorageKey({ id, type: "approveHandbookTemplate" });

  dispatch(handBookActions.setIsLoading({ key, status: true }));
  handBookApi
    .approveTemplate({ id })
    .then(() => {
      dispatch(handBookActions.invalidateKey());
      onSuccess?.();
    })
    .catch(errorCatcher)
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const createHandbookTemplateSection =
  (vals: ICreateTemplateSection, onSuccess?: () => void) => (dispatch: Dispatch) => {
    dispatch(handBookActions.setIsLoading({ key: "createTemplateSection", status: true }));
    handBookApi
      .createTemplateSection(vals)
      .then(() => {
        dispatch(handBookActions.invalidateKey());
        onSuccess?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: "createTemplateSection", status: false }));
      });
  };

export const deleteHandbookTemplateSection =
  (id: number, onSuccess?: () => void) => (dispatch: Dispatch, getState: () => RootState) => {
    const key = generateStorageKey({ id, type: "mutateHandbookTemplateSection" });
    dispatch(handBookActions.setIsLoading({ key, status: true }));
    handBookApi
      .deleteTemplateSection(id)
      .then(() => {
        dispatch(handBookActions.invalidateKey());
        onSuccess?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key, status: false }));
      });
  };

export const editHandbookTemplateSection =
  (id: number, data: ICreateTemplateSection, onSuccess?: () => void) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const key = generateStorageKey({ id, type: "mutateHandbookTemplateSection" });
    dispatch(handBookActions.setIsLoading({ key, status: true }));
    handBookApi
      .patchTemplateSection(id, data)
      .then(() => {
        dispatch(handBookActions.invalidateKey());
        onSuccess?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key, status: false }));
      });
  };

export const getHandbookTemplateSections = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter);
  if (!getState().handBookReducer.templateSections[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key, status: true }));
  }
  handBookApi
    .getTemplateSectionsList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateSections({ key, templateSections: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const getHandbookTemplateSectionsMore = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey({ ...restFilter });
  const loadingKey = generateStorageKey({ ...restFilter, type: "moreTemplateSections" });
  if (!getState().handBookReducer.templateSections[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true }));
  }
  handBookApi
    .getTemplateSectionsList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateSectionsMore({ key, templateSections: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
    });
};

export const getHandbookTemplateExpenditures = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter);
  if (!getState().handBookReducer.templateExpenditures[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key, status: true }));
  }
  handBookApi
    .getTemplateExpendituresList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateExpenditures({ key, templateExpenditures: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key, status: false }));
    });
};

export const getHandbookTemplateExpendituresMore = (filter: any) => (dispatch: Dispatch, getState: () => RootState) => {
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey({ ...restFilter });
  const loadingKey = generateStorageKey({ ...restFilter, type: "moreTemplateExpenditures" });
  if (!getState().handBookReducer.templateExpenditures[key]?.results?.length) {
    dispatch(handBookActions.setIsLoading({ key: loadingKey, status: true }));
  }
  handBookApi
    .getTemplateExpendituresList(filter)
    .then(({ data }) => {
      dispatch(handBookActions.setTemplateExpendituresMore({ key, templateExpenditures: data }));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: loadingKey, status: false }));
    });
};

export const addCheckedExpendituresToTemplate =
  ({ sectionId, successCallback }: { sectionId: string; successCallback?: () => void }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const checkedHandbooks = getState().handBookReducer.editings.checked;
    const changes = getState().handBookReducer.editings.changes;
    const lastUsedKey = getState().handBookReducer._lastUsedKey;
    const allExpendituresMap = new Map(
      (getState().handBookReducer.handbooks[lastUsedKey]?.results || []).map((x) => [x.id, x])
    );
    dispatch(handBookActions.setIsLoading({ key: "addCheckedExpendituresToTemplate", status: true }));
    await Promise.allSettled(
      Object.entries(checkedHandbooks).map(([handbookId, isChecked]) => {
        if (isChecked) {
          return handBookApi
            .createTemplateExpenditure({
              section_id: +sectionId,
              manual_expenditure_id: +handbookId,
              count: changes[+handbookId]?.count,
              price: allExpendituresMap.get(+handbookId)?.price,
            })
            .catch(errorCatcher);
        }
      })
    )
      .then(() => {
        message.success("Работы добавлены в шаблон");
        dispatch(handBookActions.clearTemplateEditings());
        dispatch(handBookActions.invalidateKey());
        successCallback?.();
      })
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: "addCheckedExpendituresToTemplate", status: false }));
      });
  };

export const templateExpendituresLocalEdit =
  ({
    expenditureId,
    materialsToDelete,
    ...change
  }: HandbookExpenditureLocalChanges & {
    expenditureId: number;
  }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(
      handBookActions.localChangeTemplateItem({ id: expenditureId, lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES, change })
    );
    materialsToDelete?.forEach((m) =>
      dispatch(handBookActions.localDeleteTemplateItem({ id: +m, lvl: HANDBOOK_TEMPLATE_LVL.EXPENDITURES }))
    );
  };

export const sendTemplate =
  ({
    objectId,
    templateId,
    finallyCallback,
    successCallback,
  }: {
    objectId: string;
    templateId: number;
    finallyCallback?: () => void;
    successCallback?: () => void;
  }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const key = generateStorageKey({ type: "send", id: templateId, objectId });
    dispatch(handBookActions.setIsLoading({ status: true, key }));
    handBookApi
      .sendTemplate({ id: templateId, buildingId: objectId })
      .then(() => {
        successCallback?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        finallyCallback?.();
        dispatch(handBookActions.setIsLoading({ status: false, key }));
      });
  };

export const approveCheckedExpenditures =
  ({ successCallback }: { successCallback?: () => void }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const checkedExpenditures = getState().handBookReducer.editings.checked;
    const checkedIds = Object.entries(checkedExpenditures)
      .filter(([_, isChecked]) => isChecked)
      .map(([id, _]) => +id);
    if (!checkedIds.length) return;
    dispatch(handBookActions.setIsLoading({ key: "approveCheckedExpenditures", status: true }));
    await handBookApi
      .approveExpenditures({
        ids: checkedIds,
        status: HANDBOOK_EXPENDITURE_APPROVAL_STATUS.APPROVED,
      })
      .then(() => {
        message.success("Позиции согласованы");
        dispatch(handBookActions.clearTemplateEditings());
        dispatch(handBookActions.invalidateKey());
        successCallback?.();
      })
      .catch(errorCatcher)
      .finally(() => {
        dispatch(handBookActions.setIsLoading({ key: "approveCheckedExpenditures", status: false }));
      });
  };

export const checkAllHandbookExpenditures = (isChecked: boolean) => (dispatch: Dispatch, getState: () => RootState) => {
  const lastUsedKey = getState().handBookReducer._lastUsedKey;
  const handbooks = getState().handBookReducer.handbooks[lastUsedKey];
  const allCheckeds = Object.fromEntries(handbooks.results.map((item) => [item.id, isChecked]));
  dispatch(handBookActions.localReplaceChecked(allCheckeds));
};


export const cloneCheckedHandbookSections =
  ({ templateId, sectionId }: { templateId: string; sectionId?: string }) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    const editings = getState().handBookReducer.templateEditings;
    if (sectionId) {
      // Подсекции
      const checkedIds = Object.entries(editings.sections.checked)
        .filter(([_, isChecked]) => isChecked)
        .map(([id, _]) => ({ subsection_id: +id, parent_id: +sectionId }));
      if (!checkedIds.length) return;
      return handBookApi
        .cloneTemplateSubSections({ templateId, ids: checkedIds })
        .catch(errorCatcher)
        .then(() => {
          dispatch(handBookActions.invalidateKey());
        });
    } else {
      // Секции
      const checkedIds = Object.entries(editings.sections.checked)
        .filter(([_, isChecked]) => isChecked)
        .map(([id, _]) => ({ section_id: +id }));
      if (!checkedIds.length) return;
      return handBookApi
        .cloneTemplateSections({ templateId, ids: checkedIds })
        .catch(errorCatcher)
        .then(() => {
          dispatch(handBookActions.invalidateKey());
        });
    }
  };

export const getHandbooksSegments = () => (dispatch: Dispatch) => {
  dispatch(handBookActions.setIsLoading({ key: "getHandbooksSegments", status: true }));
  handBookApi
    .getSegments()
    .then(({ data }) => {
      dispatch(handBookActions.setSegments(data.results));
    })
    .finally(() => {
      dispatch(handBookActions.setIsLoading({ key: "getHandbooksSegments", status: false }));
    });
};
